import { Match, Show, Switch, createEffect, createResource, createSignal, onCleanup } from "solid-js";
import type { TeacherClass } from "~/features/classes/classes";
import { VotingPage } from "~/features/voting/VotingPage";
import { VotingResetModal } from "~/features/voting/VotingResetModal";
import type { VotingModule } from "~/features/voting/voting";
import { Icon } from "~/ui/Icon";
import { AddGoalModal } from "./AddGoalModal";
import { GoalLibraryModal } from "./GoalLibraryModal";
import { GoalsPoolPanel } from "./GoalsPoolPanel";
import type { GoalsCommand, GoalsModule, LibraryGoal } from "./goals";

type GoalsTab = "voting" | "goals";
type GoalsModal = "add" | "library";

function className(value: TeacherClass) {
  const school = value.teacher_school_name || value.school_name;
  return school ? `${value.name} · ${school}` : value.name;
}

export function WeeklyGoalsPage(props: { value?: TeacherClass; module: GoalsModule; votingModule: VotingModule; onProposalCountChange?: (count: number) => void }) {
  const [snapshot, { refetch }] = createResource(() => props.value?.id, (classId) => props.module.load(classId));
  const [tab, setTab] = createSignal<GoalsTab>();
  const [modal, setModal] = createSignal<GoalsModal>();
  const [resetting, setResetting] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const [message, setMessage] = createSignal("");
  let messageTimer: number | undefined;

  createEffect(() => { props.value?.id; setTab(undefined); setModal(undefined); setResetting(false); });
  createEffect(() => { const count = snapshot()?.proposals.length; if (count != null) props.onProposalCountChange?.(count); });
  onCleanup(() => messageTimer && window.clearTimeout(messageTimer));
  const activeTab = (): GoalsTab => tab() ?? (snapshot()?.votingSession && snapshot()?.votingSession?.status !== "results" ? "voting" : "goals");
  const flash = (value: string) => {
    setMessage(value);
    if (messageTimer) window.clearTimeout(messageTimer);
    messageTimer = window.setTimeout(() => setMessage(""), 3_500);
  };
  const execute = async (command: GoalsCommand, success: string) => {
    if (!props.value || busy()) return;
    setBusy(true); setError("");
    try {
      const result = await props.module.execute(props.value.id, command);
      setModal(undefined);
      await refetch();
      flash(command.type === "add-library" ? `${result.added ?? 0} Ziele aus Bibliothek hinzugefügt!` : success);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Ziele konnten nicht aktualisiert werden.");
    } finally {
      setBusy(false);
    }
  };
  const resetVoting = async () => {
    const session = snapshot()?.votingSession;
    if (!props.value || !session || busy()) return;
    setBusy(true); setError("");
    try {
      await props.votingModule.execute(props.value.id, { type: "reset", sessionId: session.id });
      setResetting(false); setTab("goals");
      await refetch();
      flash("Abstimmung zurückgesetzt");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Abstimmung konnte nicht zurückgesetzt werden.");
    } finally {
      setBusy(false);
    }
  };

  return <Show when={props.value} fallback={<div class="card"><p>Bitte wähle zuerst eine Klasse.</p></div>}>{(selectedClass) => <>
    <Show when={error()}><div class="form-error" role="alert" style={{ "margin-bottom": "12px" }}>{error()}</div></Show>
    <Show when={message()}><div class="toast-container"><div class="toast success" role="status">{message()}</div></div></Show>
    <Show when={snapshot()} fallback={<Show when={snapshot.error} fallback={<div style={{ display: "flex", "justify-content": "center", padding: "40px" }}><div class="loading-spinner" /></div>}><div class="card"><p>Fehler beim Laden der Wochen-Klassenziele</p><button class="btn btn-primary" type="button" style={{ "margin-top": "12px" }} onClick={() => void refetch()}>Erneut versuchen</button></div></Show>}>{(data) => <>
      <div class="view-tabs">
        <button class={`view-tab ${activeTab() === "voting" ? "active" : ""}`} type="button" onClick={() => setTab("voting")}><Icon name="vote" style={{ width: "16px", height: "16px" }} /> Abstimmung <Show when={data().votingSession}><span style={{ display: "inline-block", width: "8px", height: "8px", background: data().votingSession?.status === "results" ? "#22c55e" : data().votingSession?.status === "open" ? "#ef4444" : "#f59e0b", "border-radius": "50%", "margin-left": "6px", "vertical-align": "middle" }} /></Show></button>
        <button class={`view-tab ${activeTab() === "goals" ? "active" : ""}`} type="button" onClick={() => setTab("goals")}><Icon name="target" style={{ width: "16px", height: "16px" }} /> Ziel-Pool <span style={{ opacity: 0.7, "font-size": "0.85em" }}>({data().goals.length})</span></button>
      </div>
      <Switch>
        <Match when={activeTab() === "voting"}><VotingPage value={selectedClass()} module={props.votingModule} onNavigateGoals={() => setTab("goals")} onChanged={() => void refetch()} /></Match>
        <Match when={activeTab() === "goals"}><GoalsPoolPanel className={className(selectedClass())} value={data()} busy={busy()} onOpenLibrary={() => setModal("library")} onOpenAdd={() => setModal("add")} onNavigateVoting={() => setTab("voting")} onResetVoting={() => setResetting(true)} onExecute={execute} /></Match>
      </Switch>
      <Show when={modal() === "add"}><AddGoalModal categories={data().categories} busy={busy()} onClose={() => setModal(undefined)} onSave={(draft) => execute({ type: "add", draft }, "Ziel hinzugefügt!")} /></Show>
      <Show when={modal() === "library"}><GoalLibraryModal module={props.module} pool={data().goals} busy={busy()} onClose={() => setModal(undefined)} onAdd={(goals: LibraryGoal[]) => execute({ type: "add-library", goals }, "Ziele hinzugefügt!")} /></Show>
      <Show when={resetting()}><VotingResetModal busy={busy()} onClose={() => setResetting(false)} onConfirm={resetVoting} /></Show>
    </>}</Show>
  </>}</Show>;
}
